/**
 * Tema do escritório (cores definidas pelo administrador) e modo escuro
 * (preferência de cada pessoa, guardada no navegador).
 */
export type OfficeFont = "moderna" | "classica" | "humanista";
export interface OfficeTheme { brand?: string; sidebar?: string; background?: string; radius?: number; officeName?: string; font?: OfficeFont }

/** Fontes do sistema (sem baixar nada): o administrador escolhe uma das três. */
export const FONTS: Record<OfficeFont, { label: string; sans: string; display: string }> = {
  classica: { label: "Clássica (títulos com serifa)", sans: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif', display: '"Charter", "Iowan Old Style", Georgia, serif' },
  moderna: { label: "Moderna (tudo sem serifa)", sans: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif', display: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif' },
  humanista: { label: "Humanista (arredondada)", sans: '"Avenir Next", Avenir, "Segoe UI", "Helvetica Neue", Ubuntu, sans-serif', display: '"Optima", "Candara", "Avenir Next", "Segoe UI", sans-serif' },
};

/** Preferências de cada pessoa (neste navegador). */
export type ThemeMode = "claro" | "escuro" | "auto";
export type TextSize = "compacto" | "normal" | "grande";
export type Density = "compacta" | "normal" | "confortavel";
export interface Prefs { mode: ThemeMode; size: TextSize; density: Density }
export function getPrefs(): Prefs {
  try {
    const raw = JSON.parse(localStorage.getItem("vg.prefs") ?? "null") as Partial<Prefs> | null;
    const legacy = localStorage.getItem("vg.dark") === "1" ? "escuro" : "claro";
    return { mode: raw?.mode ?? legacy, size: raw?.size ?? "normal", density: raw?.density ?? "normal" };
  } catch { return { mode: "claro", size: "normal", density: "normal" }; }
}
let lastTheme: OfficeTheme | undefined;
export function setPrefs(p: Partial<Prefs>): Prefs {
  const n = { ...getPrefs(), ...p };
  try { localStorage.setItem("vg.prefs", JSON.stringify(n)); } catch { /* sem storage */ }
  applyTheme(lastTheme);
  return n;
}
if (typeof window !== "undefined" && window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => { if (getPrefs().mode === "auto") applyTheme(lastTheme); });
}

export const DEFAULT_THEME: Required<Omit<OfficeTheme, "officeName" | "font">> = {
  brand: "#1F3350", sidebar: "#18222F", background: "#F5F6F8", radius: 12,
};

function hexToRgb(h: string): [number, number, number] {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex([r, g, b]: number[]): string {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v!))).toString(16).padStart(2, "0")).join("")}`;
}
/** Mistura a cor com branco (t>0) ou preto (t<0). */
export function shade(hex: string, t: number): string {
  const c = hexToRgb(hex);
  const target = t > 0 ? 255 : 0;
  const k = Math.abs(t);
  return rgbToHex(c.map((v) => v + (target - v) * k));
}
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

export function isDark(): boolean {
  const m = getPrefs().mode;
  if (m === "auto") return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  return m === "escuro";
}

export function setDark(on: boolean): void { setPrefs({ mode: on ? "escuro" : "claro" }); }

/** O menu lateral é escuro? (decide qual versão da logo usar) */
export function sidebarIsDark(t: OfficeTheme | undefined): boolean {
  return isDark() || luminance(t?.sidebar ?? DEFAULT_THEME.sidebar) < 0.5;
}

/** Aplica o tema nas variáveis CSS. No modo escuro só a cor principal é adaptada. */
export function applyTheme(t: OfficeTheme | undefined): void {
  lastTheme = t;
  const root = document.documentElement.style;
  const cl = document.documentElement.classList;
  const brand = t?.brand ?? DEFAULT_THEME.brand;
  const dark = isDark();
  cl.toggle("dark", dark);
  const pr = getPrefs();
  cl.toggle("fs-compacto", pr.size === "compacto"); cl.toggle("fs-grande", pr.size === "grande");
  cl.toggle("dens-compacta", pr.density === "compacta"); cl.toggle("dens-confortavel", pr.density === "confortavel");
  const f = FONTS[t?.font ?? "classica"] ?? FONTS.classica;
  root.setProperty("--font-sans", f.sans);
  root.setProperty("--font-display", f.display);
  if (!dark) {
    root.setProperty("--primary", brand);
    root.setProperty("--primary-d", shade(brand, -0.18));
    root.setProperty("--primary-l", shade(brand, 0.35));
    root.setProperty("--primary-50", shade(brand, 0.9));
    root.setProperty("--primary-100", shade(brand, 0.8));
    const bg = t?.background ?? DEFAULT_THEME.background;
    root.setProperty("--bg", bg);
    root.setProperty("--surface", shade(bg, -0.03));
    root.setProperty("--surface-2", shade(bg, 0.5));
  } else {
    for (const v of ["--primary", "--primary-d", "--primary-l", "--primary-50", "--primary-100", "--bg", "--surface", "--surface-2"]) root.removeProperty(v);
    root.setProperty("--primary", shade(brand, 0.35));
    root.setProperty("--primary-d", shade(brand, 0.5));
  }
  const side = t?.sidebar ?? DEFAULT_THEME.sidebar;
  const sideIsDark = luminance(side) < 0.5;
  root.setProperty("--side", dark ? shade(side, -0.4) : side);
  root.setProperty("--side-2", sideIsDark ? shade(side, 0.08) : shade(side, -0.07));
  root.setProperty("--side-text", sideIsDark ? "#C4CCD8" : "#344054");
  root.setProperty("--side-muted", sideIsDark ? "#7D889A" : "#667085");
  root.setProperty("--side-strong", sideIsDark ? "#FFFFFF" : "#101828");
  root.setProperty("--side-line", sideIsDark ? shade(side, 0.12) : shade(side, -0.1));
  root.setProperty("--r-card", `${t?.radius ?? DEFAULT_THEME.radius}px`);
}
